import { Injectable } from '@angular/core';
import { AngularFirestore, DocumentData } from '@angular/fire/compat/firestore';
import { Department, DepartmentUser, DepartmentWithUsers } from '../interfaces/organization.interface';
import { Observable, from, map, of } from 'rxjs';
import { UserProfile } from '../interfaces/user-profile.interface';
import { UserStatus } from '../interfaces/user-status.enum';

@Injectable({
  providedIn: 'root'
})
export class OrganizationService {
  constructor(private firestore: AngularFirestore) {}

  getDepartments(): Observable<Department[]> {
    return this.firestore.collection<Department>('departments')
      .valueChanges({ idField: 'id' });
  }

  getDepartmentById(id: string | undefined): Observable<Department | null> {
    if (!id) return of(null);
    
    return this.firestore.doc<Department>(`departments/${id}`)
      .valueChanges({ idField: 'id' })
      .pipe(map(department => department || null));
  }

  async addDepartment(department: Partial<Department>): Promise<void> {
    await this.firestore.collection('departments').add({
      ...department,
      createdAt: new Date(),
      updatedAt: new Date()
    });
  }

  async updateDepartment(id: string, department: Partial<Department>): Promise<void> {
    const departmentData = {
      ...department,
      updatedAt: new Date()
    };
    await this.firestore.doc(`departments/${id}`).update(departmentData);
  }

  async deleteDepartment(id: string): Promise<void> {
    await this.firestore.doc(`departments/${id}`).delete();
  }

  // Get users in a department
  async getUsersInDepartment(departmentId: string): Promise<UserProfile[]> {
    const departmentDoc = await this.firestore.doc(`departments/${departmentId}`).get().toPromise();
    const departmentData = departmentDoc?.data() as Department | undefined;

    if (!departmentData?.userIds?.length) {
      return [];
    }

    const userPromises = departmentData.userIds.map(async userId => {
      const userDoc = await this.firestore.doc(`users/${userId}`).get().toPromise();
      const userData = userDoc?.data() as DocumentData | undefined;
      
      if (!userData) {
        return {
          uid: userId,
          email: '',
          displayName: null,
          firstName: null,
          lastName: null,
          photoURL: null,
          phone: null,
          department: undefined,
          roles: ['user'],
          createdAt: '',
          lastLogin: '',
          status: UserStatus.PENDING
        } as UserProfile;
      }

      return {
        uid: userDoc?.id || userId,
        email: userData['email'] || '',
        displayName: userData['displayName'] || null,
        firstName: userData['firstName'] || null,
        lastName: userData['lastName'] || null,
        photoURL: userData['photoURL'] || null,
        phone: userData['phone'] || null,
        department: userData['department'] || undefined,
        roles: userData['roles'] || ['user'],
        createdAt: userData['createdAt'] || '',
        lastLogin: userData['lastLogin'] || '',
        status: userData['status'] || UserStatus.PENDING
      } as UserProfile;
    });

    return Promise.all(userPromises);
  }

  // Get departments for a user
  getDepartmentsForUser(userId: string): Observable<Department[]> {
    return this.firestore.collection<Department>('departments', ref => 
      ref.where('userIds', 'array-contains', userId)
    ).valueChanges({ idField: 'id' });
  }

  // Assign user to department
  async assignUserToDepartment(userId: string, departmentId: string): Promise<void> {
    const departmentRef = this.firestore.doc(`departments/${departmentId}`);
    const departmentDoc = await departmentRef.get().toPromise();
    const departmentData = departmentDoc?.data() as Department | undefined;
    
    const userIds = departmentData?.userIds || [];
    if (!userIds.includes(userId)) {
      userIds.push(userId);
      await departmentRef.update({ userIds });
    }
  }

  // Remove user from department
  async removeUserFromDepartment(userId: string, departmentId: string): Promise<void> {
    const departmentRef = this.firestore.doc(`departments/${departmentId}`);
    const departmentDoc = await departmentRef.get().toPromise();
    const departmentData = departmentDoc?.data() as Department | undefined;
    
    const userIds = departmentData?.userIds || [];
    const updatedUserIds = userIds.filter(id => id !== userId);
    await departmentRef.update({ userIds: updatedUserIds });
  }

  // Get department with users
  async getDepartmentWithUsers(departmentId: string): Promise<DepartmentWithUsers | null> {
    const departmentDoc = await this.firestore.doc(`departments/${departmentId}`).get().toPromise();
    
    if (!departmentDoc?.exists) {
      return null;
    }

    const departmentData = departmentDoc.data() as Department;
    const department: Department = {
      id: departmentDoc.id,
      name: departmentData.name,
      description: departmentData.description,
      userIds: departmentData.userIds || [],
      createdAt: departmentData.createdAt,
      updatedAt: departmentData.updatedAt
    };

    const users = await this.getUsersInDepartment(departmentId);

    return {
      ...department,
      users
    };
  }
} 